/**
 * Clinic statistics.
 *
 * GET /api/doctors/dashboard already computes today's overview, a three-month
 * comparison and a per-day breakdown. These helpers reshape that response for
 * display and provide the same calculations client-side, which is what the
 * admin views (which have no equivalent endpoint) need.
 *
 * Ported from `renderMonthlyStats` and `renderAdminAppointments`.
 */

import { monthKey, todayKey, monthDateKeys } from './dates';
import { sumCompletedRevenue } from './pricing';
import { BOOKING_STATUS } from './bookings';

/** Day classifications the dashboard colours by, matching the backend's labels. */
export const DAY_STATUS = {
  EMPTY: 'Empty',
  WEAK: 'Weak',
  NORMAL: 'Normal',
  PEAK: 'Crowded/Highest',
};

export const DAY_STATUS_LABELS = {
  [DAY_STATUS.EMPTY]: 'فارغ',
  [DAY_STATUS.WEAK]: 'ضعيف',
  [DAY_STATUS.NORMAL]: 'عادي',
  [DAY_STATUS.PEAK]: 'الأعلى',
};

export const DAY_STATUS_STYLES = {
  [DAY_STATUS.EMPTY]: 'bg-slate-800 text-slate-500 border-slate-700',
  [DAY_STATUS.WEAK]: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  [DAY_STATUS.NORMAL]: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  [DAY_STATUS.PEAK]: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
};

/** Shape returned when the dashboard endpoint has not answered yet. */
export const EMPTY_DASHBOARD = {
  todayOverview: {
    totalBookings: 0,
    pendingBookings: 0,
    completedCheckupsMonth: 0,
    revenueMonth: 0,
  },
  comparativeStats: null,
  dailyPerformance: [],
};

/** Tolerate a partial or absent dashboard payload without the UI having to. */
export function normalizeDashboard(payload) {
  if (!payload || typeof payload !== 'object') return EMPTY_DASHBOARD;
  return {
    todayOverview: { ...EMPTY_DASHBOARD.todayOverview, ...(payload.todayOverview || {}) },
    comparativeStats: payload.comparativeStats || null,
    dailyPerformance: Array.isArray(payload.dailyPerformance) ? payload.dailyPerformance : [],
  };
}

/** Totals for one month's bookings — the backend's `getMonthStats`, client-side. */
export function monthStats(bookings = [], monthStr) {
  const inMonth = bookings.filter((b) => b.date && String(b.date).startsWith(monthStr));
  const completed = inMonth.filter((b) => b.status === BOOKING_STATUS.COMPLETED);
  return {
    bookings: inMonth.length,
    completed: completed.length,
    revenue: sumCompletedRevenue(inMonth),
    activeDays: new Set(inMonth.map((b) => b.date)).size,
  };
}

/**
 * Per-day rows for a month, every day present even when it has no bookings.
 * The `PEAK` label goes to the day with the highest revenue, as on the server.
 */
export function dailyPerformance(bookings = [], monthStr) {
  const rows = new Map(
    monthDateKeys(monthStr).map((date) => [
      date,
      { date, totalBookings: 0, completedBookings: 0, dailyRevenue: 0, status: DAY_STATUS.EMPTY },
    ]),
  );

  for (const booking of bookings) {
    const row = rows.get(booking.date);
    if (!row) continue;
    row.totalBookings += 1;
    if (booking.status === BOOKING_STATUS.COMPLETED) {
      row.completedBookings += 1;
      row.dailyRevenue += Number(booking.finalPrice) || 0;
    }
  }

  const list = [...rows.values()];
  const maxRevenue = list.reduce((max, row) => Math.max(max, row.dailyRevenue), 0);

  for (const row of list) {
    if (row.totalBookings === 0) row.status = DAY_STATUS.EMPTY;
    else if (row.dailyRevenue === maxRevenue && maxRevenue > 0) row.status = DAY_STATUS.PEAK;
    else if (row.totalBookings < 3) row.status = DAY_STATUS.WEAK;
    else row.status = DAY_STATUS.NORMAL;
  }

  return list;
}

/** Today's headline numbers, computed from a booking list. */
export function todayOverview(bookings = [], now = new Date()) {
  const today = todayKey(now);
  const currentMonth = monthKey(today);

  const todayBookings = bookings.filter((b) => String(b.date) === today);
  const pendingUpcoming = bookings.filter(
    (b) => b.status === BOOKING_STATUS.PENDING && String(b.date) >= today,
  );
  const completedThisMonth = bookings.filter(
    (b) => b.date && String(b.date).startsWith(currentMonth) && b.status === BOOKING_STATUS.COMPLETED,
  );

  return {
    totalBookings: todayBookings.length,
    pendingBookings: pendingUpcoming.length,
    completedCheckupsMonth: completedThisMonth.length,
    revenueMonth: sumCompletedRevenue(completedThisMonth),
  };
}

/** Signed percentage change, guarding against a zero baseline. */
export function percentChange(current, previous) {
  const from = Number(previous) || 0;
  const to = Number(current) || 0;
  if (from === 0) return to === 0 ? 0 : 100;
  return Math.round(((to - from) / from) * 100);
}

/** Month-over-month deltas for the comparison card. */
export function compareMonths(currentStats, previousStats) {
  if (!currentStats || !previousStats) return null;
  return {
    bookings: percentChange(currentStats.bookings, previousStats.bookings),
    completed: percentChange(currentStats.completed, previousStats.completed),
    revenue: percentChange(currentStats.revenue, previousStats.revenue),
    activeDays: percentChange(currentStats.activeDays, previousStats.activeDays),
  };
}

/** Booking counts by status — used by the admin appointments view. */
export function statusBreakdown(bookings = []) {
  const counts = {};
  for (const status of Object.values(BOOKING_STATUS)) counts[status] = 0;
  for (const booking of bookings) {
    const status = booking.status || BOOKING_STATUS.PENDING;
    counts[status] = (counts[status] || 0) + 1;
  }
  return counts;
}
